/**
 * Extraction du message d'erreur d'une réponse d'API (4.56.0).
 *
 * Les handlers Nitro du projet renvoient toujours la même enveloppe :
 * `{ message, code, details? }` sous `error.data.data`. Sur un 400 de
 * validation, `message` vaut « Donnees invalides » — un libellé technique qui ne
 * dit rien au propriétaire. Le message utile, celui écrit dans le schéma Zod et
 * qui indique QUOI corriger, vit dans `details[].message`.
 *
 * Ce helper existe parce que trois appelants lisaient `message` seul et jetaient
 * `details` : `useGite`, `useEstablishment` et la page d'édition établissement
 * (qui passe par `rawFetch`, hors composable). Le refus d'URL dans un nom en
 * dépend entièrement : sans lui, il refusait sans jamais dire pourquoi ni où
 * mettre l'adresse.
 *
 * Les messages par défaut de Zod sont francisés côté serveur
 * (`server/plugins/zod-locale.ts`) — d'où l'absence de filtre sur leur contenu
 * ici : tout ce qui remonte est présentable.
 */

/** Au-delà, le message devient un pavé illisible dans un toast. */
const MAX_ISSUES = 3

export function extractApiErrorMessage(e: unknown, fallback: string): string {
  if (!e || typeof e !== 'object' || !('data' in e)) return fallback

  const errorData = e as {
    data?: { data?: { message?: string, details?: unknown } }
  }

  // `details` n'est PAS toujours un tableau : `createApiError`
  // (`server/utils/errors.ts`) le type `Record<string, unknown>`, et des routes
  // y mettent un objet (`{ invalidIds }`). Sans ce garde, `.map` lève une
  // TypeError DANS le catch — l'erreur d'origine disparaît au profit d'un crash.
  const raw = errorData.data?.data?.details
  if (!Array.isArray(raw)) return errorData.data?.data?.message || fallback

  const seen = new Set<string>()
  const messages: string[] = []

  for (const issue of raw as Array<{ message?: string, path?: unknown[] }>) {
    const message = issue?.message
    if (!message) continue

    // Le champ en préfixe : sans lui, une soumission avec deux liens invalides
    // affichait « URL invalide URL invalide » sans dire lequel des deux.
    const field = Array.isArray(issue.path) ? issue.path[0] : undefined
    const line = typeof field === 'string' ? `${field} : ${message}` : message

    if (seen.has(line)) continue
    seen.add(line)
    messages.push(line)
  }

  if (!messages.length) return errorData.data?.data?.message || fallback

  const shown = messages.slice(0, MAX_ISSUES).join(' ')
  return messages.length > MAX_ISSUES
    ? `${shown} (+${messages.length - MAX_ISSUES})`
    : shown
}
